import { useState, useCallback } from 'react';

const DEFAULT_REACT_FILES = {
  'App.jsx': `function App() {
  const [count, setCount] = React.useState(0);

  return (
    <div style={{ padding: '40px', fontFamily: 'sans-serif', textAlign: 'center' }}>
      <h1 style={{ color: '#2563eb', marginBottom: '20px' }}>Hello React! 👋</h1>
      <p style={{ fontSize: '16px', color: '#666', marginBottom: '30px' }}>
        Edit this file and click Run to see changes.
      </p>
      <button
        onClick={() => setCount(count + 1)}
        style={{
          padding: '10px 20px',
          fontSize: '16px',
          backgroundColor: '#2563eb',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer'
        }}
      >
        Count: {count}
      </button>
    </div>
  );
}`,
  'index.jsx': `const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);`,
  'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>React Preview</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
        'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
        sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    #root {
      width: 100%;
      height: 100%;
    }
  </style>
</head>
<body>
  <div id="root"></div>
</body>
</html>`,
  'package.json': `{
  "name": "react-interview-app",
  "version": "1.0.0",
  "type": "module",
  "dependencies": {
    "react": "^18.2.0",
    "react-dom": "^18.2.0"
  }
}`,
};

const DEFAULT_ANGULAR_FILES = {
  'app.component.ts': `@Component({
  selector: 'app-root',
  template: \`
    <div style="padding: 40px; font-family: sans-serif; text-align: center;">
      <h1 style="color: #d97706; margin-bottom: 20px;">Hello Angular! 🚀</h1>
      <p style="font-size: 16px; color: #666; margin-bottom: 10px;">
        Component Name: {{ title }}
      </p>
      <p style="font-size: 14px; color: #999;">
        Edit this file and click Run to see changes.
      </p>
      <div style="margin-top: 30px; padding: 20px; background-color: #f3f4f6; border-radius: 5px;">
        <p style="color: #374151; margin-bottom: 10px;">✨ Template rendering enabled</p>
        <p style="color: #6b7280; font-size: 12px;">
          This is a safe preview mode for Angular during interviews.
        </p>
      </div>
    </div>
  \`,
  styles: []
})
export class AppComponent {
  title = 'Angular Interview App';
}`,
  'main.ts': `// Bootstrap application
bootstrapApplication(AppComponent)
  .catch(err => console.error(err));`,
  'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Angular Preview</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
        'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
        sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    app-root {
      display: block;
      width: 100%;
      height: 100%;
    }
  </style>
</head>
<body>
  <app-root></app-root>
</body>
</html>`,
  'package.json': `{
  "name": "angular-interview-app",
  "version": "1.0.0",
  "dependencies": {
    "@angular/core": "^17.0.0",
    "@angular/platform-browser": "^17.0.0",
    "@angular/platform-browser-dynamic": "^17.0.0"
  }
}`,
};

export const useUIVirtualFS = (language) => {
  const defaultFiles = language === 'react' ? DEFAULT_REACT_FILES : DEFAULT_ANGULAR_FILES;

  const [files, setFiles] = useState(defaultFiles);
  const [activeFile, setActiveFileState] = useState(Object.keys(defaultFiles)[0]);

  const updateFile = useCallback((fileName, content) => {
    setFiles(prev => ({
      ...prev,
      [fileName]: content,
    }));
  }, []);

  const addFile = useCallback((fileName, content = '') => {
    setFiles(prev => ({
      ...prev,
      [fileName]: content,
    }));
  }, []);

  const deleteFile = useCallback((fileName) => {
    setFiles(prev => {
      const updated = { ...prev };
      delete updated[fileName];
      return updated;
    });

    // If deleted file was active, switch to first remaining file
    if (activeFile === fileName) {
      const remainingFiles = Object.keys(files).filter(f => f !== fileName);
      if (remainingFiles.length > 0) {
        setActiveFileState(remainingFiles[0]);
      }
    }
  }, [activeFile, files]);

  const resetFiles = useCallback(() => {
    setFiles(defaultFiles);
    setActiveFileState(Object.keys(defaultFiles)[0]);
  }, [defaultFiles]);

  const getFileContent = useCallback((fileName) => {
    return files[fileName] || '';
  }, [files]);

  const getAllFiles = useCallback(() => {
    return files;
  }, [files]);

  return {
    files,
    activeFile,
    setActiveFile: setActiveFileState,
    updateFile,
    addFile,
    deleteFile,
    resetFiles,
    getFileContent,
    getAllFiles,
  };
};
